import { productFruits } from 'product-fruits';
import { nextTick, readonly, ref } from "vue";
import { User } from "@/models/User.model";
import { Organisation } from "@/models/Organisation.model";
import config from '@/config/app.config';

const WORKSPACE_CODE = 'veX3ZtegNUYdFkiv';

// Product Fruits must only be booted once per session: the package doesn't guard against repeated
// init() calls (it injects its script again every time) and offers no way to remove an event listener.
let initialized = false;
let unreadCountListenerAttached = false;

// The layout (and with it the announcements button) is re-created on every route change, so we keep
// track of the most recent trigger element and (re)attach the news widget to it whenever it changes.
let announcementsTrigger: HTMLElement | null = null;

const unreadAnnouncementsCount = ref(0);

export const announcementsCount = readonly(unreadAnnouncementsCount);

// The payload shape isn't in the package types (the API is injected by the remote script), so accept
// the documented `{ count }` as well as a bare number or a CustomEvent-style wrapper.
function extractUnreadCount(payload: unknown): number | null {
	if (typeof payload === 'number') {
		return payload;
	}

	if (payload && typeof payload === 'object') {
		const { count, detail } = payload as { count?: unknown; detail?: unknown };

		if (typeof count === 'number') {
			return count;
		}

		if (detail !== undefined) {
			return extractUnreadCount(detail);
		}
	}

	return null;
}

function attachToCurrentTrigger() {
	const announcementsV2 = window.productFruits?.api?.announcementsV2;
	if (!announcementsV2 || !announcementsTrigger?.isConnected) {
		return;
	}

	// Must be registered before the first attach, otherwise the initial count is never emitted.
	if (!unreadCountListenerAttached) {
		announcementsV2.listen('newsfeed-unread-count-changed', (...args: unknown[]) => {
			const count = extractUnreadCount(args[0]);
			if (count !== null) {
				unreadAnnouncementsCount.value = count;
			}
		});
		unreadCountListenerAttached = true;
	}

	announcementsV2.attachNewsWidgetToElement(announcementsTrigger);
}

/**
 * Boot Product Fruits for the given user and organisation. Safe to call repeatedly: only the first call
 * with a complete, non-impersonated session has any effect.
 */
export function initializeProductFruits(me: User | null | undefined, organisation: Organisation | null | undefined, locale: string) {
	if (initialized || !me || !organisation || me.impersonating) {
		return;
	}
	const currentLanguage = locale.substring(0, 2).toLowerCase();

	let userIdPrefix = '';
	switch (config.ENV) {
		case 'development':
			userIdPrefix = 'dev-';
			break;
		case 'test':
			userIdPrefix = 'test-';
			break;
		case 'staging':
			userIdPrefix = 'stag-';
			break;
		case 'production':
		default:
			userIdPrefix = '';
			break;
	}

	const userId = userIdPrefix + organisation.id + '-' + me.id;
	// get signup date in ISO yyyy-mm-dd formta
	const signUpAt = me.getCurrentOrganisationRole()?.createdAt || '';

	productFruits.init(WORKSPACE_CODE, currentLanguage, {
		username: userId,
		email: me.email || '',
		role: me.getCurrentOrganisationRole()?.role || '',
		signUpAt: signUpAt,
		props: {
			subscriptionPlan: organisation.subscription_plan || '',
			subscriptionType: organisation.subscription_type || '',
			environment: config.ENV,
		}
	});
	initialized = true;

	// A trigger may already have been registered before init (e.g. the layout mounted first).
	productFruits.safeExec(() => attachToCurrentTrigger());
}

/**
 * Use as a function ref on the announcements button: Vue calls it with the element on mount, which also
 * covers the button being rendered conditionally. The null passed on unmount is ignored — we don't want
 * the old layout's unmount to clear the new layout's trigger — and a detached trigger is never attached.
 */
export function setAnnouncementsTrigger(element: Element | null) {
	const trigger = element instanceof HTMLElement ? element : null;
	if (!trigger || trigger === announcementsTrigger) {
		return;
	}

	announcementsTrigger = trigger;

	if (initialized) {
		// Vue calls function refs while it is still building the layout, before the layout is inserted into
		// the document. Once Product Fruits is ready safeExec runs synchronously, so wait for the render to
		// finish or the trigger would still be detached and never get attached.
		nextTick(() => productFruits.safeExec(() => attachToCurrentTrigger()));
	}
}
